import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  CreateProposalPolicyData,
  DuplicateProposalPolicyBody,
  PricePolicyImportRelativeTime,
  ProposalPolicyApi,
} from 'api';

export const findProposalPolicy = createAsyncThunk(
  'proposalPolicy/findProposalPolicy',
  async (option: Record<string, any>, { rejectWithValue }) => {
    try {
      const response = await ProposalPolicyApi.find(option);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const createProposalPolicy = createAsyncThunk(
  'proposalPolicy/createProposalPolicy',
  async (body: CreateProposalPolicyData, { rejectWithValue }) => {
    try {
      const response = await ProposalPolicyApi.create(body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const updateProposalPolicy = createAsyncThunk(
  'proposalPolicy/updateProposalPolicy',
  async (params: any, { rejectWithValue }) => {
    try {
      const { id, body } = params;
      const response = await ProposalPolicyApi.update(id, body);

      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

export const duplicateProposalPolicy = createAsyncThunk(
  'productObjectMedia/duplicateProposalPolicy',
  async (
    params: { id: string; body: DuplicateProposalPolicyBody },
    { rejectWithValue }
  ) => {
    try {
      const { id, body } = params;
      const response = await ProposalPolicyApi.duplicate(id, body);
      return response;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  }
);

interface IState {
  tableList: Array<any>;
  list: Array<any>;
  reloadFlag: boolean;
  googleSheetUrl: string;
  sheetName: string;
  importMethod: number;
  importType: number;
  importTime: PricePolicyImportRelativeTime;
  isAutoImportEnabled: boolean;
  policyCell: string;
  savedPagination: any;
  savedList: Array<any>;
}

const initialState: IState = {
  tableList: [],
  list: [],
  reloadFlag: false,
  googleSheetUrl: '',
  sheetName: '',
  importType: 0,
  importMethod: 2,
  importTime: {},
  isAutoImportEnabled: true,
  policyCell: '',
  savedPagination: { current: 1, next: 2, prev: 0, take: 10, total: 0 },
  savedList: [],
};

const proposalPolicySlice = createSlice({
  name: 'proposalPolicy',
  initialState,
  reducers: {
    reloadProposalPolicy(state) {
      state.reloadFlag = !state.reloadFlag;
    },
    setImportPropertyProposalPolicy(state, action) {
      const {
        googleSheetUrl,
        sheetName,
        importMethod,
        importType,
        importTime,
        isAutoImportEnabled,
      } = action.payload;

      state.googleSheetUrl = googleSheetUrl;
      state.sheetName = sheetName;
      state.importMethod = importMethod;
      state.importType = importType;
      state.importTime = importTime;
      state.isAutoImportEnabled = isAutoImportEnabled;
      state.policyCell = DEFAULT_CELL_TO_CREATE_PROPOSAL_POLICY;
    },
    updateSavedPaginationProposalPolicy(state, action) {
      state.savedPagination = action.payload;
    },
    updateSavedListProposalPolicy(state, action) {
      state.savedList = action.payload;
    },
    clearImportSettingProposalPolicy(state) {
      state.googleSheetUrl = '';
    },
  },
  extraReducers: builder => {
    builder.addCase(findProposalPolicy.fulfilled, (state, action) => {
      const { data } = action.payload;

      const newData = data.map(item => {
        return {
          ...item,
          key: item.id,
          value: item.id,
        };
      });

      state.list = newData;
    });
  },
});

const { actions, reducer: proposalPolicyReducer } = proposalPolicySlice;

export const DEFAULT_CELL_TO_CREATE_PROPOSAL_POLICY = 'D4';
export const DEFAULT_PRODUCT_CODE_CELL_TO_CREATE_PROPOSAL_POLICY = 'A4';

export const {
  reloadProposalPolicy,
  setImportPropertyProposalPolicy,
  updateSavedPaginationProposalPolicy,
  updateSavedListProposalPolicy,
  clearImportSettingProposalPolicy,
} = actions;

export default proposalPolicyReducer;
